import type { PlaybookRoutingGroupRule } from "@repo/app-types/entities/company";
import type { GroupId } from "@repo/app-types/entities/group";
import type {
  AdminApiPlaybook,
  PlaybookNode,
} from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { DragHandle } from "@repo/ui/DraggableList/dragHandle";
import { DraggableItem } from "@repo/ui/DraggableList/draggableItem";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Size } from "@repo/ui/helpers/helpers";
import { useDraggableList } from "@repo/ui/hooks/useDraggableList";
import { IconCopy, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { Link, useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  usePlaybookRoutingQuery,
  useUpdatePlaybookRoutingMutation,
} from "../../../api/entities/company.js";
import { useAllTeammateGroupsQuery } from "../../../api/entities/group.js";
import {
  useAllMinimalPlaybooksQuery,
  useAllPlaybooksQuery,
  useCreatePlaybookMutation,
  useDeletePlaybookMutation,
} from "../../../api/entities/playbook.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageLayout } from "../../../components/layout/pageLayout.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSection } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  RoutingRuleCard,
  type RoutingPlaybookOption,
} from "./routingRuleCard.js";

/**
 * Creates a deep copy of playbook nodes with new unique IDs.
 */
function cloneNodesWithNewIds(nodes: readonly PlaybookNode[]): PlaybookNode[] {
  return nodes.map((node) => {
    if (node.type === "section") {
      return {
        ...node,
        id: uuidv4(),
        nodes: cloneNodesWithNewIds(node.nodes),
      };
    } else {
      return {
        ...node,
        id: uuidv4(),
      };
    }
  });
}

export const PlaybooksSettingsSection: FC = () => {
  const navigate = useNavigate();
  const { data: playbooks, isLoading } = useAllPlaybooksQuery({
    exclude_teammate_agent_playbooks: "true",
  });
  const [deletePlaybook] = useDeletePlaybookMutation();
  const [createPlaybook] = useCreatePlaybookMutation();

  // Playbook template being deleted
  const [deletingPlaybookId, setDeletingPlaybookId] = useState<
    string | undefined
  >(undefined);

  // Playbook template being duplicated
  const [duplicatingPlaybookId, setDuplicatingPlaybookId] = useState<
    string | undefined
  >(undefined);

  const handleCreateNew = () => {
    navigate(appPaths.settingsNewPlaybook.build());
  };

  const handleEditTemplate = (template: AdminApiPlaybook) => {
    navigate(appPaths.settingsPlaybook.build({ playbookId: template.id }));
  };

  const handleDuplicatePlaybook = async () => {
    if (duplicatingPlaybookId === undefined) return;

    const playbook = playbooks?.find((p) => p.id === duplicatingPlaybookId);
    if (!playbook) return;

    await createPlaybook({
      name: `Copy of ${playbook.name}`,
      assessment_tree: {
        nodes: cloneNodesWithNewIds(playbook.assessment_tree.nodes),
      },
      knowledge_base_ids: playbook.settings.knowledge_base?.ids ?? [],
      outcomes: playbook.settings.outcomes,
    });

    setDuplicatingPlaybookId(undefined);
  };

  const renderRows = () => {
    if (isLoading) return <TableSkeleton />;
    if (!playbooks || playbooks.length === 0)
      return (
        <SettingsTableEmptyBodyContent colSpan={3}>
          No playbooks created yet.
        </SettingsTableEmptyBodyContent>
      );
    return playbooks.map((a) => (
      <tr
        key={a.id}
        className="group relative cursor-pointer hover:bg-gray-50"
        onClick={(event) => {
          if (event.defaultPrevented) {
            return;
          }

          handleEditTemplate(a);
        }}
      >
        <SettingsTableDataCell>
          {a.name}

          <div
            className="absolute top-1/2 right-3 hidden -translate-y-1/2 space-x-2 text-muted-foreground group-hover:flex"
            onClick={(e) => e.preventDefault()}
          >
            <IconButton
              variant={ButtonVariant.Tertiary}
              size={Size.SMALL}
              icon={<IconPencil className="size-4" />}
              accessibleLabel={`Edit ${a.name}`}
              onClick={() => handleEditTemplate(a)}
            />
            <IconButton
              variant={ButtonVariant.Tertiary}
              size={Size.SMALL}
              icon={<IconCopy className="size-4" />}
              accessibleLabel={`Duplicate ${a.name}`}
              onClick={() => setDuplicatingPlaybookId(a.id)}
            />
            <IconButton
              variant={ButtonVariant.Tertiary}
              size={Size.SMALL}
              icon={<IconTrash className="size-4" />}
              accessibleLabel={`Delete ${a.name}`}
              onClick={() => setDeletingPlaybookId(a.id)}
            />
          </div>
        </SettingsTableDataCell>
      </tr>
    ));
  };

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Form}
        title={settingsSections.playbooks.label}
        actions={
          <Button
            size={Size.SMALL}
            variant={ButtonVariant.Primary}
            icon={<IconPlus className="size-4" />}
            onClick={handleCreateNew}
          >
            Create playbook
          </Button>
        }
      />

      <SimpleModal
        isOpen={deletingPlaybookId !== undefined}
        onCancel={() => setDeletingPlaybookId(undefined)}
        variant={SimpleModalVariant.Warning}
        title="Delete assessment template"
        subtitle="Are you sure you want to delete this assessment template?"
        confirmButtonText="Delete"
        onConfirm={() => {
          if (deletingPlaybookId !== undefined) {
            deletePlaybook(deletingPlaybookId);
          }
          setDeletingPlaybookId(undefined);
        }}
      />

      <SimpleModal
        isOpen={duplicatingPlaybookId !== undefined}
        onCancel={() => setDuplicatingPlaybookId(undefined)}
        variant={SimpleModalVariant.Standard}
        title="Duplicate playbook"
        subtitle="A new playbook will be created with the same sections and criteria as this playbook."
        confirmButtonText="Duplicate"
        onConfirm={handleDuplicatePlaybook}
      />

      <SettingsSection>
        <SettingsTable>
          <SettingsTableHeaderSection>
            <tr>
              <SettingsTableHeaderCell className="w-[60%]">
                Name
              </SettingsTableHeaderCell>
            </tr>
          </SettingsTableHeaderSection>
          <SettingsTableBodySection>{renderRows()}</SettingsTableBodySection>
        </SettingsTable>

        <RoutingRulesSection />
      </SettingsSection>
    </PageLayout>
  );
};

const RoutingRulesSection: FC = () => {
  const dispatch = useAppDispatch();
  const { data: rules, isLoading: isLoadingRules } = usePlaybookRoutingQuery();
  const { data: groups, isLoading: isLoadingGroups } =
    useAllTeammateGroupsQuery();
  const { data: playbooks, isLoading: isLoadingPlaybooks } =
    useAllMinimalPlaybooksQuery();
  const [updateRules] = useUpdatePlaybookRoutingMutation();

  // State for adding a new rule
  const [isAddingRule, setIsAddingRule] = useState(false);

  const isLoading = isLoadingRules || isLoadingGroups || isLoadingPlaybooks;

  const groupOptions: SimpleSelectOption<GroupId>[] = useMemo(() => {
    if (!groups) {
      return [];
    }

    return [...groups]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((group) => ({
        label: group.name,
        value: group.id,
      }));
  }, [groups]);

  // Groups already used in existing rules (for filtering in new rule creation)
  const usedGroupIds = useMemo(() => {
    if (!rules) {
      return new Set();
    }

    return new Set(rules.map((r) => r.group_id));
  }, [rules]);

  // Available groups for new rules (excludes already-used groups)
  const availableGroupOptions = useMemo(
    () => groupOptions.filter((g) => !usedGroupIds.has(g.value)),
    [groupOptions, usedGroupIds],
  );

  const playbookOptions: RoutingPlaybookOption[] = useMemo(() => {
    if (!playbooks) {
      return [];
    }

    return [...playbooks]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((playbook) => ({
        label: playbook.name,
        value: playbook.id,
        createdAt: playbook.created_at,
      }));
  }, [playbooks]);

  // Get available group options for editing a specific rule
  // (excludes groups used by other rules, but includes the current rule's group)
  const getGroupOptionsForRule = (
    ruleIndex: number,
  ): SimpleSelectOption<GroupId>[] => {
    const currentGroupId = rules?.[ruleIndex]?.group_id;
    return groupOptions.filter(
      (g) => g.value === currentGroupId || !usedGroupIds.has(g.value),
    );
  };

  const handleStartAddingRule = () => {
    if (!availableGroupOptions.length || !playbookOptions.length) return;
    setIsAddingRule(true);
  };

  const handleCancelAddRule = () => {
    setIsAddingRule(false);
  };

  const handleReorderRules = async (
    reorderedRules: readonly PlaybookRoutingGroupRule[],
  ) => {
    try {
      await updateRules({ rules: [...reorderedRules] }).unwrap();
      showNotification(dispatch, {
        id: `routing-rules-reordered-${Date.now()}`,
        primaryMessage: "Routing rules reordered successfully",
        type: NotificationType.SUCCESS,
      });
    } catch (err: any) {
      showNotification(dispatch, {
        id: `routing-rules-reorder-error-${Date.now()}`,
        primaryMessage: `Failed to reorder routing rules: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  const {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleDragLeave,
    dragOverIndex,
  } = useDraggableList({
    items: rules ?? [],
    onReorder: (reorderedRules) => void handleReorderRules(reorderedRules),
  });

  const handleSaveNewRule = async (newRule: PlaybookRoutingGroupRule) => {
    const currentRules = rules ?? [];

    try {
      await updateRules({ rules: [...currentRules, newRule] }).unwrap();
      showNotification(dispatch, {
        id: `routing-rule-created-${Date.now()}`,
        primaryMessage: "Routing rule created successfully",
        type: NotificationType.SUCCESS,
      });
      setIsAddingRule(false);
    } catch (err: any) {
      showNotification(dispatch, {
        id: `routing-rule-create-error-${Date.now()}`,
        primaryMessage: `Failed to create routing rule: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  const handleUpdateRule = async (
    index: number,
    updatedRule: PlaybookRoutingGroupRule,
  ) => {
    if (!rules) {
      return;
    }

    const newRules = [...rules];
    newRules[index] = updatedRule;

    try {
      await updateRules({ rules: newRules }).unwrap();
      showNotification(dispatch, {
        id: `routing-rule-updated-${Date.now()}`,
        primaryMessage: "Routing rule updated successfully",
        type: NotificationType.SUCCESS,
      });
    } catch (err: any) {
      showNotification(dispatch, {
        id: `routing-rule-update-error-${Date.now()}`,
        primaryMessage: `Failed to update routing rule: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  const handleDeleteRule = async (ruleIndex: number) => {
    if (!rules) {
      return;
    }

    const newRules = rules.filter((_, index) => index !== ruleIndex);

    try {
      await updateRules({ rules: newRules }).unwrap();
      showNotification(dispatch, {
        id: `routing-rule-deleted-${Date.now()}`,
        primaryMessage: "Routing rule deleted successfully",
        type: NotificationType.SUCCESS,
      });
    } catch (err: any) {
      showNotification(dispatch, {
        id: `routing-rule-delete-error-${Date.now()}`,
        primaryMessage: `Failed to delete routing rule: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="mt-8 border-t border-border-light pt-8">
        <h3 className="text-lg font-semibold text-foreground">Routing</h3>
        <div className="mt-4">
          <TableSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 border-t border-border-light pt-8">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Playbook routing
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            When calls are{" "}
            <a
              href="https://dev.alpharun.com/api-reference/endpoint/upload-recording"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              uploaded
            </a>{" "}
            without a playbook, these rules run from top to bottom. The first
            rule that matches the teammate&apos;s group chooses which playbook
            to use. When a rule includes multiple playbooks, Alpharun
            automatically assigns each call to the best fitting playbook by
            comparing it against each playbook&apos;s criteria.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {rules?.map((rule, index) => (
          <DraggableItem
            dragOverIndex={dragOverIndex}
            index={index}
            key={rule.group_id}
            onDragEnd={handleDragEnd}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
          >
            <div className="group relative w-full">
              <DragHandle className="absolute top-1/2 -left-8 flex h-10 w-5 -translate-y-1/2 items-center justify-center text-muted-foreground/60 opacity-0 transition-opacity group-hover:opacity-100 hover:text-muted-foreground">
                <span className="grid grid-cols-2 gap-0.5" aria-hidden="true">
                  {Array.from({ length: 6 }).map((_, dotIndex) => (
                    <span
                      key={dotIndex}
                      className="size-0.5 rounded-full bg-current"
                    />
                  ))}
                </span>
              </DragHandle>
              <RoutingRuleCard
                groupOptions={getGroupOptionsForRule(index)}
                playbookOptions={playbookOptions}
                selectedGroupId={rule.group_id}
                selectedPlaybookIds={
                  rule.kind === "single"
                    ? [rule.playbook_id]
                    : rule.playbook_ids
                }
                selectedDefaultPlaybookId={
                  rule.kind === "single" ? undefined : rule.default_playbook_id
                }
                onSave={(updatedRule) => handleUpdateRule(index, updatedRule)}
                onDelete={() => handleDeleteRule(index)}
              />
            </div>
          </DraggableItem>
        ))}

        {isAddingRule ? (
          <RoutingRuleCard
            groupOptions={availableGroupOptions}
            playbookOptions={playbookOptions}
            selectedGroupId={undefined}
            selectedPlaybookIds={[]}
            selectedDefaultPlaybookId={undefined}
            onSave={handleSaveNewRule}
            onCancelNew={handleCancelAddRule}
            isNew
          />
        ) : groupOptions.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            To create a routing rule, first{" "}
            <Link
              to={appPaths.settingsTeammates.build()}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              create a teammate group
            </Link>{" "}
            (ex. &ldquo;Sales advisors&rdquo;) that will be routed to a specific
            playbook.
          </p>
        ) : availableGroupOptions.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            All teammate groups have been assigned a routing rule.{" "}
            <Link
              to={appPaths.settingsTeammates.build()}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Create another teammate group
            </Link>{" "}
            to add more routing rules.
          </p>
        ) : (
          <Button
            variant={ButtonVariant.Secondary}
            icon={<IconPlus className="size-4" />}
            onClick={handleStartAddingRule}
            isDisabled={!playbookOptions.length}
            className="mt-2"
          >
            Add a routing rule
          </Button>
        )}
      </div>
    </div>
  );
};
